<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { onClickOutside, useMagicKeys } from '@vueuse/core'
import { useCompanyStore } from '../stores/company-store'

export interface EnrichCandidate {
  name?: string
  description?: string
  website?: string
  sector?: string
  investment_stage?: string
}

const props = withDefaults(
  defineProps<{
    modelValue: string
    label?: string
    placeholder?: string
    property?: string
    v$?: any
    showLookup?: boolean
    lookupLoading?: boolean
  }>(),
  {
    placeholder: '',
    v$: {},
    showLookup: false,
    lookupLoading: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'select', company: { id: string; name: string }): void
  (e: 'clear'): void
  (e: 'lookup'): void
}>()

const companyStore = useCompanyStore()

const MAX_RESULTS = 20

///////////////////////////////////////////////////////////////////////////////
// Search
///////////////////////////////////////////////////////////////////////////////

const isOpen = ref(false)
const highlightedIndex = ref(-1)

const results = computed(() => {
  const q = props.modelValue?.trim().toLowerCase()
  if (!q || q.length < 2) return []
  return companyStore.companies.filter((c) => c.name.toLowerCase().includes(q)).slice(0, MAX_RESULTS)
})

const selecting = ref(false)

watch(
  () => props.modelValue,
  () => {
    if (selecting.value) return
    isOpen.value = (props.modelValue?.trim().length ?? 0) >= 2
    highlightedIndex.value = -1
  },
)

///////////////////////////////////////////////////////////////////////////////
// Selection
///////////////////////////////////////////////////////////////////////////////

const select = (company: { id: string; name: string }) => {
  selecting.value = true
  emit('update:modelValue', company.name)
  emit('select', company)
  isOpen.value = false
  highlightedIndex.value = -1
  nextTick(() => {
    selecting.value = false
  })
}

const onInput = (e: Event) => {
  const value = (e.target as HTMLInputElement).value
  emit('update:modelValue', value)
  emit('clear')
}

///////////////////////////////////////////////////////////////////////////////
// Keyboard Navigation
///////////////////////////////////////////////////////////////////////////////

const wrapper = ref<HTMLElement | null>(null)
const resultsList = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

onClickOutside(wrapper, () => {
  isOpen.value = false
})

const { escape } = useMagicKeys()
watch(escape, (v) => {
  if (v) isOpen.value = false
})

const onKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value || !results.value.length) return

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      highlightedIndex.value = Math.min(highlightedIndex.value + 1, results.value.length - 1)
      scrollToHighlighted()
      break
    case 'ArrowUp':
      e.preventDefault()
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
      scrollToHighlighted()
      break
    case 'Enter':
      e.preventDefault()
      if (highlightedIndex.value >= 0 && highlightedIndex.value < results.value.length) {
        select(results.value[highlightedIndex.value])
      }
      break
  }
}

const scrollToHighlighted = () => {
  nextTick(() => {
    const list = resultsList.value
    if (!list) return
    const el = list.children[highlightedIndex.value] as HTMLElement
    if (el) el.scrollIntoView({ block: 'nearest' })
  })
}

const error = computed(() => props.v$[props.property]?.$errors[0]?.$message)

const focusInput = () => {
  inputRef.value?.focus()
}
</script>

<template>
  <fieldset :id="property">
    <label v-if="label" class="block cursor-pointer text-sm font-medium text-gray-700" @click.prevent="focusInput">
      {{ label }}
    </label>
    <div ref="wrapper" class="relative" :class="{ 'mt-1': label }">
      <div class="relative mt-1">
        <input
          ref="inputRef"
          type="text"
          :value="modelValue"
          class="block w-full rounded-md border border-gray-300 shadow-sm placeholder:italic focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          :class="[
            error ? 'border-red-300 focus:border-red-300 focus:ring-red-200' : '',
            showLookup || lookupLoading ? 'pr-10' : '',
          ]"
          :placeholder="placeholder"
          @input="onInput"
          @keydown="onKeydown"
          @focus="isOpen = (modelValue?.trim().length ?? 0) >= 2"
        />
        <!-- Loading spinner -->
        <div v-if="lookupLoading" class="absolute inset-y-0 right-0 flex items-center pr-3">
          <svg
            class="h-4 w-4 animate-spin text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
        <!-- Lookup arrow button -->
        <button
          v-if="showLookup && !lookupLoading"
          type="button"
          class="lookup-arrow absolute inset-y-0 right-1.5 my-auto flex h-7 w-7 items-center justify-center rounded-md bg-sky-500 text-white hover:bg-sky-600 active:bg-sky-700"
          title="Search the web for this company"
          @click="emit('lookup')"
        >
          <svg
            class="h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2.5"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </button>
      </div>

      <ul
        v-show="isOpen && results.length"
        ref="resultsList"
        class="absolute z-50 mt-1 max-h-[280px] w-full min-w-[220px] overflow-y-auto rounded-md bg-white shadow-lg ring-[1px] ring-gray-700/20"
      >
        <li
          v-for="(item, idx) in results"
          :key="item.id"
          class="relative flex cursor-pointer items-center whitespace-nowrap px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
          :class="idx === highlightedIndex ? 'bg-gray-100' : ''"
          @click="select(item)"
          @mouseenter="highlightedIndex = idx"
        >
          <div class="font-medium">{{ item.name }}</div>
        </li>
      </ul>
    </div>
    <p v-if="error" class="mt-1 text-sm text-red-500">{{ error }}</p>
  </fieldset>
</template>

<style scoped>
.lookup-arrow {
  animation: slide-in 200ms ease-out;
}

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
